import { motion } from "framer-motion";
import {
  Brain,
  Heart,
  Scale,
  Briefcase,
  GraduationCap,
  BookOpen,
  Users,
  Award,
  ExternalLink,
  Calendar,
  ArrowRight,
  MessageSquare,
  Microscope,
  Globe,
  Search,
  TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import { 
  cn, 
  type Service, 
  type Qualification, 
  type Publication, 
  ROUTE_PATHS, 
  hoverLift 
} from "@/lib/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const iconMap: Record<string, React.ElementType> = {
  brain: Brain,
  heart: Heart,
  scale: Scale,
  briefcase: Briefcase,
  graduation: GraduationCap,
  book: BookOpen,
  users: Users,
  award: Award,
  message: MessageSquare,
  science: Microscope,
  globe: Globe,
  search: Search,
  trendingup: TrendingUp,
};

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const Icon = iconMap[service.iconName] || Brain;

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full flex flex-col border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary/5">
        <CardHeader className="space-y-4">
          <div className="space-y-2">
            <Badge variant="secondary" className="capitalize text-[10px] tracking-wider font-mono">
              {service.category}
            </Badge>
            <CardTitle className="font-serif text-2xl text-foreground">
              {service.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-grow space-y-4">
          <p className="text-muted-foreground leading-relaxed">
            {service.shortDescription}
          </p>
          
          {service.category === 'szkolenia' && service.businessImpact && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Korzyści dla uczestnika:</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {service.benefits.slice(0, 2).map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Wpływ na biznes:</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {service.businessImpact.slice(0, 2).map((impact, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1 h-1 bg-accent rounded-full mt-2 flex-shrink-0" />
                      {impact}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="pt-0">
          <Button 
            asChild 
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <a 
              href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              {service.category === 'mediacje' ? 'Skontaktuj się i opisz swoje potrzeby' : 
               service.category === 'szkolenia' ? 'Skontaktuj się i omów szczegóły' : 'Umów wizytę'}
              {service.category === 'mediacje' || service.category === 'szkolenia' ? 
               <MessageSquare className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
            </a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

interface QualificationCardProps {
  qualification: Qualification;
}

export function QualificationCard({ qualification }: QualificationCardProps) {
  const categoryLabels = {
    academic: "Akademickie",
    professional: "Doświadczenie",
    certification: "Certyfikacja",
  };

  return (
    <div className="relative pl-8 pb-8 last:pb-0 group">
      {/* Timeline Line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-border group-last:h-4" />
      
      {/* Timeline Dot */}
      <div className="absolute left-[-4px] top-1.5 w-2 h-2 rounded-full bg-primary border-4 border-background ring-4 ring-primary/10" />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">

          <Badge variant="outline" className="text-[10px] uppercase tracking-widest bg-muted/30">
            {categoryLabels[qualification.category]}
          </Badge>
        </div>
        <h4 className="text-lg font-semibold text-foreground leading-snug">
          {qualification.title}
        </h4>
        <p className="text-sm text-muted-foreground font-medium">
          {qualification.institution}
        </p>
        {qualification.description && (
          <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-2xl">
            {qualification.description}
          </p>
        )}
      </div>
    </div>
  );
}

interface PublicationCardProps {
  publication: Publication;
}

export function PublicationCard({ publication }: PublicationCardProps) {
  const typeIcons = {
    ksiazka: BookOpen,
    artykul: Microscope,
    konferencja: Users,
    media: Globe,
  };

  const Icon = typeIcons[publication.type];

  return (
    <Card className="group border-none shadow-none bg-transparent hover:bg-muted/30 transition-colors duration-300 rounded-xl overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-4 space-y-0">
        <div className="shrink-0 w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
          <Icon size={18} />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">
              {publication.year}
            </span>
            <span className="text-muted-foreground/30 text-xs">•</span>
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent">
              {publication.type}
            </span>
          </div>
          <CardTitle className="font-serif text-xl leading-tight group-hover:text-primary transition-colors">
            {publication.title}
          </CardTitle>
          {publication.publisher && (
            <p className="text-sm italic text-muted-foreground">
              {publication.publisher}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="pl-14">
        <p className="text-sm text-muted-foreground/90 leading-relaxed">
          {publication.description}
        </p>
      </CardContent>
      {publication.url && (
        <CardFooter className="pl-14 pt-0">
          <a
            href={publication.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
          >
            Zobacz źródło
            <ExternalLink size={12} />
          </a>
        </CardFooter>
      )}
    </Card>
  );
}
